<template>
  <section class="post-detail-wrap">
    <div class="pdheader">
      <div class="left">
        <div class="cover" :style="{ backgroundImage: coverStyle }" />
        <div class="author-meta">
          <span class="name">{{ data?.author?.nickname || '-' }}</span>
          <span class="identity" v-if="showIdentityTag">{{ data?.author?.identity }}</span>
        </div>
      </div>
    </div>
    <div class="pdbanners">
      <video
        v-if="videoSource"
        class="video-banner"
        controls
        playsinline
        preload="metadata"
        :poster="videoPoster"
      >
        <source :src="videoSource" />
      </video>
      <div v-else-if="imageMediaItems.length > 0" class="media-carousel">
        <div class="media-track" ref="mediaTrack" @scroll.passive="onMediaScroll">
          <div class="media-slide" v-for="(item, index) in imageMediaItems" :key="`${item.url}-${index}`">
            <div class="banner" :style="{ backgroundImage: `url(${mediaCover(item.url)})` }" />
          </div>
        </div>
        <div class="media-pagination" v-if="imageMediaItems.length > 1">
          <button
            v-for="(item, index) in imageMediaItems"
            :key="`dot-${item.url}-${index}`"
            class="dot"
            :class="{ active: currentMediaIndex === index }"
            @click="scrollToMedia(index)"
          />
        </div>
      </div>
      <div class="txtbanner" v-else>
        <div class="bg" />
        <span>{{ data?.title || '宠一帖子分享' }}</span>
      </div>
    </div>
    <div class="pdcontent">
      <div class="title">{{ data?.title || '-' }}</div>
      <div class="desc" v-if="data?.content" v-html="presetHtmlText(data?.content)" />
      <div class="pet-list" v-if="pets.length > 0">
        <div class="pet-card" v-for="pet in pets" :key="pet.id || pet.petId || pet.nickname">
          <div class="pet-avatar" :style="{ backgroundImage: `url(${petCover(pet)})` }" />
          <div class="pet-meta">
            <div class="pet-name">{{ pet.nickname || '-' }}</div>
            <div class="pet-breed">{{ petBreedText(pet) }}</div>
          </div>
        </div>
      </div>
      <div class="deploytime">发布于 {{ publishTime }}</div>
    </div>

    <div class="pdcomments" v-if="comments.length > 0 || data?.commentCount > 0">
      <div class="comment-title">{{ commentTitle }}</div>
      <div class="comment-item" v-for="comment in comments" :key="comment.commentId">
        <div class="comment-avatar" :style="{ backgroundImage: `url(${commentAvatar(comment.user)})` }" />
        <div class="comment-main">
          <div class="comment-name">{{ comment.user?.nickname || '-' }}</div>
          <div class="comment-content">{{ comment.content || '-' }}</div>
          <div class="comment-images" v-if="comment.images?.length > 0">
            <div
              class="comment-image"
              v-for="(image, index) in comment.images"
              :key="`${image}-${index}`"
              :style="{ backgroundImage: `url(${setStaticUrl(image)})` }"
            />
          </div>
          <div class="comment-meta">
            <span>{{ commentTime(comment.createdTime) }}</span>
          </div>
          <div class="reply-list" v-if="comment.replies?.length > 0">
            <div class="reply-item" v-for="reply in comment.replies" :key="reply.commentId">
              <div class="reply-avatar" :style="{ backgroundImage: `url(${commentAvatar(reply.user)})` }" />
              <div class="reply-main">
                <div class="comment-name">{{ reply.user?.nickname || '-' }}</div>
                <div class="comment-content">
                  <template v-if="reply.replyUser?.nickname">
                    <span class="reply-prefix">回复 {{ reply.replyUser?.nickname }}：</span>
                  </template>
                  <span>{{ reply.content || '-' }}</span>
                </div>
                <div class="comment-images" v-if="reply.images?.length > 0">
                  <div
                    class="comment-image"
                    v-for="(image, index) in reply.images"
                    :key="`${image}-${index}`"
                    :style="{ backgroundImage: `url(${setStaticUrl(image)})` }"
                  />
                </div>
                <div class="comment-meta">
                  <span>{{ commentTime(reply.createdTime) }}</span>
                </div>
              </div>
            </div>
            <div class="reply-more" v-if="comment.replyHasMore">展开更多</div>
          </div>
        </div>
      </div>
    </div>
    <div class="downloadbtn" @click="openChongyiDownload"><span>打开 宠一APP</span></div>
  </section>
</template>

<script setup>
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import moment from 'moment';
  import * as Request from '@/assets/request.js';
  import { getSearchByKey, openChongyiDownload, presetHtmlText, setStaticUrl } from '@/assets/utils.js';

  const id = getSearchByKey('id')
  const data = ref(null)
  const comments = ref([])
  const mediaTrack = ref(null)
  const currentMediaIndex = ref(0)

  const coverStyle = computed(() => {
    const avatar = setStaticUrl(data.value?.author?.avatar || '')
    return avatar ? `url(${avatar})` : ''
  })

  const postType = computed(() => `${data.value?.postType || 'image'}`)

  const mediaItems = computed(() => {
    const mediaInfo = data.value?.mediaInfo
    if (Array.isArray(mediaInfo) && mediaInfo.length > 0) {
      return mediaInfo.filter(item => `${item?.url || ''}`.trim())
    }
    const images = data.value?.images || []
    return images
      .map(item => `${item || ''}`.trim())
      .filter(Boolean)
      .map(url => ({ url, type: 'image' }))
  })

  const isVideoMedia = (item) => `${item?.type || 'image'}` === 'video'

  const validMediaItems = computed(() => {
    const items = mediaItems.value
    return items.filter(item => postType.value === 'video' ? isVideoMedia(item) : !isVideoMedia(item))
  })

  const videoMedia = computed(() => validMediaItems.value.find(item => isVideoMedia(item)) || null)
  const videoSource = computed(() => {
    const source = `${videoMedia.value?.video || videoMedia.value?.url || ''}`.trim()
    return source ? setStaticUrl(source) : ''
  })
  const videoPoster = computed(() => mediaCover(videoMedia.value?.url || ''))
  const imageMediaItems = computed(() => validMediaItems.value.filter(item => !isVideoMedia(item)))

  const publishTime = computed(() => {
    const raw = data.value?.publishedTime
    return raw ? moment(raw).format('YYYY.MM.DD HH:mm') : '-'
  })

  const pets = computed(() => data.value?.pets || [])

  const showIdentityTag = computed(() => {
    const identity = `${data.value?.author?.identity || ''}`.trim()
    return identity && identity !== '普通'
  })

  const commentTitle = computed(() => `${data.value?.commentCount ?? comments.value.length ?? 0}条评论`)

  const petCover = (pet) => {
    return setStaticUrl(pet?.avatar || pet?.images?.[0] || '')
  }

  const mediaCover = (url) => {
    return setStaticUrl(url || '')
  }

  const petBreedText = (pet) => {
    const parts = [`${pet?.breed || ''}`.trim()]
    return parts.filter(Boolean).join(' / ') || '-'
  }

  const commentAvatar = (user) => {
    return setStaticUrl(user?.avatar || '')
  }

  const commentTime = (time) => {
    return time ? moment(time).format('MM.DD HH:mm') : '-'
  }

  const getData = async () => {
    if (!id) return
    const result = await Request.post({
      url: '/post/detail',
      data: {
        postId: id,
      }
    }, true)
    if (!result || result.error) return
    data.value = result.data
  }

  const getComments = async () => {
    if (!id) return
    const result = await Request.post({
      url: '/post/commentList',
      data: {
        postId: id,
        pageSize: 10,
      }
    }, true)
    if (!result || result.error) return
    comments.value = result.data?.list || []
  }

  const onMediaScroll = () => {
    const track = mediaTrack.value
    if (!track) return
    const width = track.clientWidth || 1
    currentMediaIndex.value = Math.round(track.scrollLeft / width)
  }

  const scrollToMedia = (index) => {
    const track = mediaTrack.value
    if (!track) return
    track.scrollTo({
      left: track.clientWidth * index,
      behavior: 'smooth',
    })
    currentMediaIndex.value = index
  }

  watch(imageMediaItems, async () => {
    currentMediaIndex.value = 0
    await nextTick()
    const track = mediaTrack.value
    if (!track) return
    track.scrollLeft = 0
  })

  onMounted(() => {
    getData()
    getComments()
  })
</script>

<style scoped lang="less">
@import "index.less";
</style>
